/* @flow */


const DEBUG_KEY = '__jsapi_debugconsole';
const PARTNER_DEBUG_KEY = '__jsapi_debugpartners';

/**
 * @description Helper to get cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {string} - cookie value
 */
function getCookie(
  name: string = '',
  doc?: Document = window.document
): string {
  let ret = '';
  if (name.length > 0 && doc.cookie) {
    const m =
      // eslint-disable-next-line unicorn/prefer-string-raw
      doc.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]*)\\s*(;|$)`) || [];
    if (m.length >= 3) {
      ret = decodeURIComponent(m[2]);
    }
  }
  return ret;
}

/**
 * @description Helper to get query param value from the current URL
 * @param {string} name - query param key
 * @param {Location} loc - window.location (for testability)
 * @returns {string} - param value (decoded) or empty string
 */
function getQueryParam(
  name: string = '',
  loc: Location = window.location
): string {
  if (!name || !loc || !loc.href) {
    return '';
  }
  try {
    const url = new URL(loc.href);
    return url.searchParams.get(name)
      ? decodeURIComponent(url.searchParams.get(name) || '')
      : '';
  } catch {
    // Fallback regex parsing if URL constructor is unavailable
    const m = loc.href.match(new RegExp(`[?&]${name}=([^&#]*)`));
    return m && m[1] ? decodeURIComponent(m[1]) : '';
  }
}

/**
 * @description Build a lower-cased Set of partner names from URL and cookie
 * @returns {Set<string>} - set of partners (lower-cased). '*' enables all partners
 */
function getDebugPartnersSet(): Set<string> {
  const values: Array<string> = [];
  const fromUrl = getQueryParam(PARTNER_DEBUG_KEY);
  const fromCookie = getCookie(PARTNER_DEBUG_KEY);
  if (fromUrl && fromUrl.length) {
    values.push(fromUrl);
  }
  if (fromCookie && fromCookie.length) {
    values.push(fromCookie);
  }
  const set = new Set();
  values
    .join(',')
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .forEach(s => set.add(s.toLowerCase()));
  return set;
}

// Use window.location.href (standard) - always available in jsdom/test environments
// Reference: window.location is the standard way to access location, document.location
// is just a reference to it. Using window.location directly is more reliable.
const debugConsole: boolean =
  (typeof window !== 'undefined' &&
    window.location &&
    window.location.href &&
    window.location.href.includes(`${DEBUG_KEY}=1`)) ||
  getCookie(DEBUG_KEY) === '1';

/**
 * Utility to check whether debugging is enabled/not.
 * Need to add '__jsapi_debugconsole=1' to the web page
 * URL query params
 *
 * @returns {boolean} - Debugging is enabled or not
 */
export default function isdebugEnabled(): boolean {
  return debugConsole;
}

const debugPartners: Set<string> = getDebugPartnersSet();

/**
 * @description Utility to check whether partner-scoped debugging is enabled
 * Requires global debug to be enabled and partner name to be listed in
 * '__jsapi_debugpartners' (URL or cookie). A value of '*' enables all partners.
 * Matching is case-insensitive.
 *
 * @param {string} partnerName - bidder/partner identifier (e.g., 'a9v2', 'openx')
 * @returns {boolean} - Partner debugging is enabled or not
 */
export function isdebugPartnerEnabled(partnerName: string): boolean {
  if (!isdebugEnabled()) {
    return false;
  }
  const name = String(partnerName || '').toLowerCase();
  if (!name) {
    return false;
  }
  return debugPartners.has('*') || debugPartners.has(name);
}

// Allow access via default export as a property for compatibility
// $FlowFixMe
(isdebugEnabled: any).isdebugPartnerEnabled = isdebugPartnerEnabled;
